// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import './SourceDistribution.scss';
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

// Register required chart components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const SourceDistribution = ({ logs }) => {
  const { t } = useTranslation();
  
  // Process logs to extract source distribution
  const sourceData = useMemo(() => {
    if (!logs || logs.length === 0) return { sources: {}, total: 0 };
    
    const sources = {};
    
    logs.forEach(log => {
      const source = log.source || 'unknown';
      sources[source] = (sources[source] || 0) + 1;
    });
    
    return {
      sources,
      total: logs.length
    };
  }, [logs]);
  
  // Calculate percentages for each source
  const sourceStats = useMemo(() => {
    return Object.entries(sourceData.sources)
      .map(([source, count]) => ({
        source,
        count,
        percentage: sourceData.total > 0 
          ? ((count / sourceData.total) * 100).toFixed(1) 
          : 0
      }))
      .sort((a, b) => b.count - a.count);
  }, [sourceData]);
  
  // Prepare chart data
  const chartData = useMemo(() => {
    const labels = sourceStats.map(item => item.source);
    const data = sourceStats.map(item => item.count);
    const backgroundColors = sourceStats.map(item => {
      switch(item.source.toLowerCase()) {
        case 'dashboard': return 'rgba(63, 185, 80, 0.8)';
        case 'stamp': return 'rgba(187, 128, 9, 0.8)';
        case 'assistant': return 'rgba(219, 97, 162, 0.8)';
        default: return `rgba(${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, 0.8)`;
      }
    });
    
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: backgroundColors,
        borderColor: '#ffffff',
        borderWidth: 2,
      }]
    };
  }, [sourceStats]);
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        padding: 12,
        borderColor: '#e1e4e8',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const value = context.raw;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '60%',
  };
  
  // Render empty state if no logs
  if (!logs || logs.length === 0) {
    return (
      <div className="source-distribution-chart empty-state">
        <h3>{t('source_distribution') || 'Source Distribution'}</h3>
        <p>{t('no_source_data') || 'No source data available for the selected filters.'}</p>
      </div>
    );
  }
  
  // Display top 3 sources in stats, group others
  const displayStats = sourceStats.length <= 4 
    ? sourceStats 
    : [...sourceStats.slice(0, 3), {
        source: 'others',
        count: sourceStats.slice(3).reduce((sum, item) => sum + item.count, 0),
        percentage: sourceStats.slice(3)
          .reduce((sum, item) => sum + parseFloat(item.percentage), 0)
          .toFixed(1)
      }];
    
  return (
    <div className="source-distribution-chart">
      <h3>{t('source_distribution') || 'Source Distribution'}</h3>
      <div className="chart-container">
        <Doughnut data={chartData} options={chartOptions} />
      </div>
      <div className="source-stats">
        {displayStats.map((item) => (
          <div 
            key={item.source} 
            className={`source-stat-item source-${item.source.toLowerCase()}`}
          >
            <div className="source-name">{item.source}</div>
            <div className="source-value">
              <span className="count">{item.count}</span>
              <span className="percentage">({item.percentage}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SourceDistribution; 